// Signin.js
import React, { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { UserContext } from '../userContext/UserContext';
import { getSasToken } from '../../services/apiService';
import { loginRequest, apiTokenRequest } from '../../authConfig';
import Logo from '../../assets/images/logo.png';
import MicrosoftLogo from '../../assets/images/microsoft.png';
import './signin.css';

const Signin = () => {
    const navigate = useNavigate();
    const { instance, inProgress } = useMsal();
    const {
        setUserName, setUserId, setRole,
        setSasToken, setSasTokenExpiry,
        setIsAuthenticated, setAuthReady, setSasError,
    } = useContext(UserContext);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    const handleMicrosoftSignin = async () => {
        if (inProgress !== InteractionStatus.None) {
            setError('A sign-in is already in progress. Please wait.');
            return;
        }
        setIsLoading(true);
        setError('');
        try {
            // Microsoft login popup — use extraScopesToConsent to pre-consent the API
            // scope without mixing audiences in the token request itself
            const loginResponse = await instance.loginPopup({
                scopes: loginRequest.scopes,
                extraScopesToConsent: apiTokenRequest.scopes,
            });

            const account = loginResponse.account;
            instance.setActiveAccount(account);
            const userRole = account.idTokenClaims?.roles?.[0] || 'Role.User';

            // Update user context and local storage with user info
            setUserName(account.name);
            setUserId(account.username);
            setRole(userRole);

            localStorage.setItem('userName', account.name);
            localStorage.setItem('userId', account.username);
            localStorage.setItem('role', userRole);

            // Fetch and store SAS token from your API
            const tokenResult = await getSasToken();
            setSasToken(tokenResult.sasToken);
            setSasTokenExpiry(tokenResult.expiresOn);
            localStorage.setItem('sasToken', tokenResult.sasToken);
            localStorage.setItem('sasTokenExpiry', tokenResult.expiresOn);

            // Clear any prior error state and set authenticated BEFORE navigating
            setSasError(false);
            setAuthReady(true);
            setIsAuthenticated(true);
            navigate('/dashboard');
        } catch (error) {
            console.error('Error during Microsoft sign-in or SAS token fetching:', error);
            if (error.errorCode === 'user_cancelled') {
                setError('Sign-in was cancelled.');
            } else {
                setError('Sign-in failed. Please try again.');
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="signin-container">
            <header className="signin-header">
                <img src={Logo} alt="SPM Logo" className="signin-logo" />
                <div className="title">Open Issues</div>
            </header>

            <div className="signin-content">
                {error && (
                    <div className="signin-error" role="alert">
                        {error}
                    </div>
                )}
                <button
                    className="microsoft-button"
                    onClick={handleMicrosoftSignin}
                    disabled={isLoading || inProgress !== InteractionStatus.None}
                >
                    <img src={MicrosoftLogo} alt="Microsoft logo" className="microsoft-logo" />
                    <span className="microsoft-text">{isLoading ? 'Signing in...' : 'Sign in with Microsoft'}</span>
                </button>
            </div>
        </div>
    );
};

export default Signin;
